// reCAPTCHA utility functions
// Handles token generation and validation for both reCAPTCHA v3 and v2

const RECAPTCHA_V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
const RECAPTCHA_V2_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY;

// Testing mode - set to true to force v3 failure for testing v2 fallback
const FORCE_V3_FAILURE = process.env.REACT_APP_FORCE_RECAPTCHA_V3_FAILURE === 'true'

/**
 * Check if reCAPTCHA v3 is available and properly configured
 * @returns {boolean} True if reCAPTCHA v3 is ready to use
 */
export const isRecaptchaV3Available = () => {
  return !!(window.grecaptcha && RECAPTCHA_V3_SITE_KEY);
};

/**
 * Check if reCAPTCHA v2 is available and properly configured
 * @returns {boolean} True if reCAPTCHA v2 is ready to use
 */
export const isRecaptchaV2Available = () => {
  return !!(window.grecaptcha && RECAPTCHA_V2_SITE_KEY);
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use isRecaptchaV3Available instead
 */
export const isRecaptchaAvailable = isRecaptchaV3Available;

/**
 * Generate a reCAPTCHA v3 token for the specified action
 * @param {string} action - The action name for reCAPTCHA scoring (default: 'start_session')
 * @returns {Promise<string|null>} The reCAPTCHA token or null if failed
 */
export const generateRecaptchaToken = async (action = 'start_session') => {
  if (FORCE_V3_FAILURE) {
    return null;
  }

  if (!isRecaptchaV3Available()) {
    return null;
  }

  try {
    const token = await window.grecaptcha.execute(RECAPTCHA_V3_SITE_KEY, { action });
    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Get reCAPTCHA headers for API requests
 * @param {string|null} token - The reCAPTCHA token (optional, will generate if not provided)
 * @param {string} version - reCAPTCHA version ('v3' or 'v2', defaults to 'v3')
 * @returns {Promise<Object>} Headers object with reCAPTCHA data
 */
export const getRecaptchaHeaders = async (token = null, version = 'v3') => {
  const headers = {
    'X-Recaptcha-Version': version
  };

  if (token) {
    headers['X-Recaptcha-Token'] = token;
  } else if (version === 'v3') {
    const generatedToken = await generateRecaptchaToken();
    if (generatedToken) {
      headers['X-Recaptcha-Token'] = generatedToken;
    }
  }

  return headers;
};

/**
 * Get reCAPTCHA v2 site key
 * @returns {string|null} The reCAPTCHA v2 site key
 */
export const getRecaptchaV2SiteKey = () => {
  return RECAPTCHA_V2_SITE_KEY;
};

/**
 * Enhanced reCAPTCHA token generation with v2 fallback capability
 * This function attempts v3 first, and can trigger v2 fallback through a callback
 * @param {string} action - The action name for reCAPTCHA scoring
 * @param {Function} onV3Failed - Callback function to trigger v2 fallback modal
 * @returns {Promise<{success: boolean, token: string|null, version: string, requiresV2: boolean}>}
 */
export const generateRecaptchaTokenWithFallback = async (action = 'start_session', onV3Failed = null) => {
  const v3Token = await generateRecaptchaToken(action);
  
  if (v3Token) {
    return {
      success: true,
      token: v3Token,
      version: 'v3',
      requiresV2: false
    };
  }

  if (onV3Failed && isRecaptchaV2Available()) {
    onV3Failed();
    return {
      success: false,
      token: null,
      version: 'v2',
      requiresV2: true
    };
  }

  return {
    success: false,
    token: null,
    version: null,
    requiresV2: false
  };
};

export const testingUtils = {
  isTestingMode: () => FORCE_V3_FAILURE,
  getStatus: () => ({
    v3Available: isRecaptchaV3Available(),
    v2Available: isRecaptchaV2Available(),
    v3SiteKey: RECAPTCHA_V3_SITE_KEY ? '✅ Configured' : '❌ Missing',
    v2SiteKey: RECAPTCHA_V2_SITE_KEY ? '✅ Configured' : '❌ Missing',
    forcingV3Failure: FORCE_V3_FAILURE,
    globalModalHandler: !!window.__showRecaptchaV2Modal
  }),
  triggerV2Modal: () => {
    if (window.__showRecaptchaV2Modal) {
      window.__showRecaptchaV2Modal(
        (token) => console.log('Test: reCAPTCHA v2 verification successful:', token),
        (error) => console.error('Test: reCAPTCHA v2 verification failed:', error)
      );
    }
  }
};

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.__recaptchaTestingUtils = testingUtils;
}